import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreditCard, Check, Zap, Building2, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function BillingPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPlan, setNewPlan] = useState({ name: '', type: 'clinic_monthly', price: '', features: '', triage_limit: '', patient_volume_limit: '' });

  useEffect(() => {
    Promise.all([
      API.get('/billing/plans'),
      API.get('/billing/subscriptions'),
      API.get('/billing/usage')
    ]).then(([pl, sub, us]) => {
      setPlans(pl.data || []);
      setSubscriptions(sub.data || []);
      setUsage(us.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const subscribe = async (planId) => {
    try {
      await API.post('/billing/subscriptions', { plan_id: planId });
      toast.success('Subscribed successfully');
      const sub = await API.get('/billing/subscriptions');
      setSubscriptions(sub.data || []);
    } catch (err) { toast.error('Subscription failed'); }
  };

  const createPlan = async () => {
    try {
      await API.post('/billing/plans', {
        ...newPlan,
        price: parseFloat(newPlan.price) || 0,
        features: newPlan.features.split(',').map(f => f.trim()).filter(Boolean),
        triage_limit: newPlan.triage_limit ? parseInt(newPlan.triage_limit) : null,
        patient_volume_limit: newPlan.patient_volume_limit ? parseInt(newPlan.patient_volume_limit) : null,
      });
      toast.success('Plan created');
      setCreateDialogOpen(false);
      const pl = await API.get('/billing/plans');
      setPlans(pl.data || []);
    } catch (err) { toast.error('Failed to create plan'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  const activeSub = subscriptions.find(s => s.status === 'active');

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="billing-title" className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Billing</h1>
          <p className="text-slate-500 mt-1">Manage your subscription and usage</p>
        </div>
        {user?.role === 'super_admin' && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-plan-btn" className="bg-sky-600 hover:bg-sky-700"><Zap className="w-4 h-4 mr-2" /> Create Plan</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle style={{fontFamily: 'Manrope'}}>Create Billing Plan</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Plan Name</Label><Input data-testid="plan-name" value={newPlan.name} onChange={e => setNewPlan({...newPlan, name: e.target.value})} placeholder="Enterprise Plus" /></div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={newPlan.type} onValueChange={v => setNewPlan({...newPlan, type: v})}>
                    <SelectTrigger data-testid="plan-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clinic_monthly">Clinic Monthly</SelectItem>
                      <SelectItem value="hospital_enterprise">Hospital Enterprise</SelectItem>
                      <SelectItem value="pay_as_you_go">Pay As You Go</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Price ($)</Label><Input data-testid="plan-price" type="number" value={newPlan.price} onChange={e => setNewPlan({...newPlan, price: e.target.value})} /></div>
                <div className="space-y-2"><Label>Features (comma-separated)</Label><Input data-testid="plan-features" value={newPlan.features} onChange={e => setNewPlan({...newPlan, features: e.target.value})} placeholder="Feature 1, Feature 2" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Triage Limit</Label><Input type="number" value={newPlan.triage_limit} onChange={e => setNewPlan({...newPlan, triage_limit: e.target.value})} placeholder="Unlimited" /></div>
                  <div className="space-y-2"><Label>Patient Limit</Label><Input type="number" value={newPlan.patient_volume_limit} onChange={e => setNewPlan({...newPlan, patient_volume_limit: e.target.value})} placeholder="Unlimited" /></div>
                </div>
                <Button data-testid="submit-plan" onClick={createPlan} className="w-full bg-sky-600 hover:bg-sky-700">Create Plan</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {usage && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[
            { label: 'Monthly Triages', value: usage.month_triages },
            { label: 'Today Triages', value: usage.today_triages },
            { label: 'Total Patients', value: usage.total_patients },
            { label: 'Total Triages', value: usage.total_triages },
          ].map((s, i) => (
            <div key={i} className="stat-card" data-testid={`usage-stat-${i}`}>
              <p className="text-sm font-medium text-slate-500">{s.label}</p>
              <p className="text-3xl font-bold text-slate-900 mt-1" style={{fontFamily: 'Manrope'}}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {activeSub && (
        <Card className="border-sky-200 bg-sky-50">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Current Plan: {activeSub.plan_name}</p>
                <p className="text-sm text-slate-500">Active since {new Date(activeSub.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="plans-grid">
        {plans.map((plan) => {
          const isSubscribed = subscriptions.some(s => s.plan_id === plan.id && s.status === 'active');
          return (
            <Card key={plan.id} data-testid={`plan-${plan.id}`} className={`border-slate-200 ${isSubscribed ? 'ring-2 ring-sky-500' : ''}`}>
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  {plan.type === 'hospital_enterprise' ? <Building2 className="w-5 h-5 text-sky-600" /> : <CreditCard className="w-5 h-5 text-sky-600" />}
                  <Badge variant="secondary" className="text-xs">{plan.type.replace(/_/g, ' ')}</Badge>
                </div>
                <CardTitle className="text-xl" style={{fontFamily: 'Manrope'}}>{plan.name}</CardTitle>
                <CardDescription>
                  <span className="text-3xl font-bold text-slate-900">${plan.price}</span>
                  <span className="text-slate-500">/{plan.type === 'pay_as_you_go' ? 'triage' : 'month'}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {plan.features?.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button
                  data-testid={`subscribe-${plan.id}`}
                  className={`w-full ${isSubscribed ? 'bg-emerald-600' : 'bg-sky-600 hover:bg-sky-700'}`}
                  disabled={isSubscribed}
                  onClick={() => subscribe(plan.id)}
                >
                  {isSubscribed ? 'Current Plan' : 'Subscribe'}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
